'use client';
import { useEffect, useRef, useState } from 'react';

let currentPlayingInstance: {
  id: string;
  pause: () => void;
  reset: () => void;
  disableAutoPlay: () => void;
} | null = null;

interface UseAudioPlayProps {
  url: string;
  id: string;
  autoPlay?: boolean;
}

interface UseAudioPlayReturn {
  isPlaying: boolean;
  progress: number;
  handlePlayPause: () => void;
  setProgress: (value: number) => void;
}

export const useAudioPlay = ({ url, id, autoPlay = false }: UseAudioPlayProps): UseAudioPlayReturn => {
  const [audio] = useState(() => (typeof window !== 'undefined' ? new Audio() : null));
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);

  const currentUrlRef = useRef(url);
  const requestAnimationFrameIdRef = useRef<number | null>(null);
  const autoPlayRef = useRef(autoPlay);

  const disableAutoPlay = () => {
    autoPlayRef.current = false;
  };

  const cleanupRAF = () => {
    if (requestAnimationFrameIdRef.current) {
      cancelAnimationFrame(requestAnimationFrameIdRef.current);
      requestAnimationFrameIdRef.current = null;
    }
  };

  const resetAudioState = () => {
    if (audio) audio.currentTime = 0;
    setProgress(0);
    setIsPlaying(false);
  };

  const pauseAudio = () => {
    if (audio) audio.pause();
    setIsPlaying(false);
    cleanupRAF();
  };

  const clearCurrentInstance = () => {
    if (currentPlayingInstance?.id === id) {
      currentPlayingInstance = null;
    }
  };

  const updateProgressWithRAF = () => {
    if (audio && !audio.paused) {
      setProgress(audio.currentTime);
      requestAnimationFrameIdRef.current = requestAnimationFrame(updateProgressWithRAF);
    }
  };

  const loadAudio = () => {
    if (!isLoaded && audio) {
      audio.src = url;
      audio.preload = 'auto';
      audio.load();
      setIsLoaded(true);
      return true;
    }
    return isLoaded;
  };

  const playAudio = async () => {
    if (!audio) return;
    try {
      const previousInstance = currentPlayingInstance;
      if (previousInstance && previousInstance.id !== id) {
        const { pause, reset, disableAutoPlay } = previousInstance;
        disableAutoPlay();
        pause();
        reset();
      }

      currentPlayingInstance = {
        id,
        pause: pauseAudio,
        reset: resetAudioState,
        disableAutoPlay,
      };

      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Failed to play audio:', error);
      pauseAudio();
      clearCurrentInstance();
    }
  };

  useEffect(() => {
    if (url !== currentUrlRef.current) {
      currentUrlRef.current = url;
      setIsLoaded(false);
      setHasEnded(false);
      pauseAudio();
      resetAudioState();
      clearCurrentInstance();
      if (autoPlayRef.current) {
        loadAudio();
      }
    }
  }, [url]);

  useEffect(() => {
    autoPlayRef.current = autoPlay;

    if (autoPlay) {
      if (currentPlayingInstance && currentPlayingInstance.id !== id) {
        const { pause, reset, disableAutoPlay } = currentPlayingInstance;
        disableAutoPlay();
        pause();
        reset();
        currentPlayingInstance = null;
      }
      setHasEnded(false);
      loadAudio();
    } else if (isPlaying) {
      pauseAudio();
      clearCurrentInstance();
    }
  }, [autoPlay]);

  useEffect(() => {
    const handleCanPlay = () => {
      if (autoPlayRef.current && !hasEnded) {
        if (currentPlayingInstance && currentPlayingInstance.id !== id) {
          const { pause, reset, disableAutoPlay } = currentPlayingInstance;
          disableAutoPlay();
          pause();
          reset();
          currentPlayingInstance = null;
        }
        playAudio();
      }
    };

    const handleEnded = () => {
      resetAudioState();
      cleanupRAF();
      clearCurrentInstance();
      setHasEnded(true);
    };

    if (!audio) return;

    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);

    if (autoPlayRef.current) {
      loadAudio();
    }

    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [hasEnded]);

  useEffect(() => {
    if (isPlaying) {
      requestAnimationFrameIdRef.current = requestAnimationFrame(updateProgressWithRAF);
    } else {
      cleanupRAF();
    }

    return cleanupRAF;
  }, [isPlaying]);

  useEffect(() => {
    return () => {
      pauseAudio();
      clearCurrentInstance();
      cleanupRAF();
      if (audio) audio.src = '';
    };
  }, []);

  const handlePlayPause = () => {
    if (isPlaying) {
      pauseAudio();
      clearCurrentInstance();
    } else {
      setHasEnded(false);
      const isReady = loadAudio();
      if (isReady) {
        playAudio();
      }
    }
  };

  const handleSetProgress = (value: number) => {
    if (!isLoaded) {
      loadAudio();
    }

    if (audio?.duration) {
      const clampedValue = Math.max(0, Math.min(audio.duration, value));
      audio.currentTime = clampedValue;
      setProgress(clampedValue);
    }
  };

  return {
    isPlaying,
    progress,
    handlePlayPause,
    setProgress: handleSetProgress,
  };
};
